/** The review queue for vendor-submitted products, across both commerce verticals. */
import type { StatusTone } from '@/components/ui/status-pill';
import { humanize } from '@/lib/format';

export type Vertical = 'FOOD' | 'PHARMACY';

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export type QueueImage = {
  id: string;
  path: string;
  /** False on a photo submitted with an edit and not yet released. */
  is_approved: boolean;
};

/**
 * One changed field, as the reviewer needs to read it: what is live now, and what
 * the vendor wants it to become. Present only for a live product carrying an edit.
 */
export type PendingField = {
  field: string;
  current: unknown;
  proposed: unknown;
};

export type PendingChanges = {
  fields: PendingField[];
  images_changed: boolean;
};

/** A fee set on the listing itself rather than inherited from anywhere. */
export type OwnFee = {
  mode: 'RATE' | 'FLAT';
  rate_pct: number | null;
  amount_inr: number | null;
};

export type QueueItem = {
  id: string;
  vertical: Vertical;
  name: string;
  brand: string | null;
  description: string | null;
  pack_size: string | null;
  category: string;
  is_available: boolean;
  is_prescription_required?: boolean;
  approval_status: ApprovalStatus;
  approval_note: string | null;
  reviewed_at: string | null;
  pending_changes: PendingChanges | null;
  images: QueueImage[];
  /** Set when the listing came from the catalogue. */
  catalog_product_id: string | null;
  /**
   * The curated photos, in order.
   *
   * A linked listing usually has none of its own, so these are what a shopper will actually
   * see — and what the row should therefore show.
   */
  catalog_images: { id: string; path: string }[];
  vendor: { id: string; business_name: string | null; city: string | null } | null;
  /**
   * Per-size inventory.
   *
   * `stock_qty: null` means the vendor does not TRACK that size, which reads as always
   * available — NOT the same as 0, an empty shelf. Showing both as "0" would tell a reviewer a
   * brand-new listing had sold out.
   */
  stock: Array<{
    variant_id: string;
    label: string;
    stock_qty: number | null;
    is_tracked: boolean;
    is_in_stock: boolean;
  }>;
  submitted_at: string;
  /**
   * When this row started waiting for a decision.
   *
   * Not the same as `submitted_at`: an edit to a two-year-old listing has been waiting since
   * the edit was written, and ageing it from the listing's birthday would report every edit
   * as years overdue.
   */
  waiting_since: string;
  /** A fee set on this listing itself. Null when it inherits from catalogue or category. */
  own_fee: OwnFee | null;
  /**
   * How many photos a SHOPPER would see — the vendor's own, or the catalogue's.
   *
   * Not `images.length`: a catalogue-linked listing usually has none of its own and shows
   * the curated gallery, so counting only the vendor's would report a product with three
   * good pictures as having none.
   */
  visible_photos: number;
};

/** Per size: the vendor's price, the fee that applies, and what a shopper pays. */
export type VariantPricing = {
  variant_id: string;
  label: string;
  vendor_price_inr: string;
  mrp_inr: string | null;
  platform_fee_inr: string;
  customer_price_inr: string;
  commission_mode: 'RATE' | 'FLAT';
  commission_rate_pct: number | null;
  commission_source: FeeSource;
  exceeds_mrp: boolean;
};

/**
 * Where a fee came from.
 *
 * The chain runs narrowest to widest, and every screen in this corner of the console reads
 * the same one: a size can override its product, a product can override its catalogue
 * master, a master can override its category, and a category with no rule means the vendor
 * keeps the lot. Naming the rung is the whole point — "10%" without "set by the Dry Food
 * default" tells an admin nothing about what changing it will touch.
 */
export type FeeSource = 'VARIANT' | 'PRODUCT' | 'CATALOG' | 'CATEGORY' | 'NONE';

/** The detail endpoint returns the queue item plus its prices. */
export type QueueDetail = QueueItem & { pricing: VariantPricing[] };

export type QueueResponse = {
  items: QueueItem[];
  total: number;
  page: number;
  page_size: number;
};

export type QueueFilters = {
  page?: number;
  page_size?: number;
  status?: ApprovalStatus | '';
  kind?: 'new' | 'edit' | '';
  vertical?: Vertical | '';
  vendor_profile_id?: string;
  q?: string;
  category?: string;
  linked?: string;
  has_own_fee?: string;
  no_photos?: string;
  prescription?: string;
  waiting_days_gte?: number | string;
  from?: string;
  to?: string;
};

/** What the tiles above the queue count. */
export type ApprovalCounts = {
  awaiting_first_review: number;
  edits_waiting: number;
  needs_review: number;
  overdue: number;
  rejected: number;
  prescription_pending: number;
  /** Never-published submissions with no photo — these cannot be approved at all. */
  blocked_no_photo: number;
  oldest_wait_days: number;
};

export const VERTICALS: readonly Vertical[] = ['FOOD', 'PHARMACY'];

export const QUEUE_SORT_FIELDS = ['submitted_at', 'updated_at', 'name'] as const;

export const APPROVAL_STATUSES: readonly ApprovalStatus[] = ['PENDING', 'APPROVED', 'REJECTED'];

export const VERTICAL_LABEL: Record<Vertical, string> = {
  FOOD: 'Food & products',
  PHARMACY: 'Medicines',
};

/**
 * What the reviewer is actually looking at.
 *
 * A live product with a queued edit is neither "pending" nor plainly "approved" —
 * customers still see the last approved version while the change waits. Collapsing
 * that into one of the two would make the reviewer think their decision is more
 * urgent, or less, than it is.
 */
export type QueueKind = 'edit' | 'new' | 'rejected' | 'live';

export function queue_kind(item: QueueItem): QueueKind {
  if (item.pending_changes) return 'edit';
  if (item.approval_status === 'PENDING') return 'new';
  if (item.approval_status === 'REJECTED') return 'rejected';
  return 'live';
}

export const KIND_LABEL: Record<QueueKind, string> = {
  edit: 'Edit waiting',
  new: 'New listing',
  rejected: 'Sent back',
  live: 'Live',
};

export const KIND_TONE: Record<QueueKind, StatusTone> = {
  edit: 'warning',
  new: 'info',
  rejected: 'destructive',
  live: 'success',
};

/** One line saying what the reviewer is being asked for. */
export function asks_for(item: QueueItem): string {
  const p = item.pending_changes;
  if (!p) {
    if (item.approval_status === 'PENDING') {
      return publish_blocked(item)
        ? 'Cannot be published — no photo'
        : 'Publish this listing for the first time';
    }
    return KIND_LABEL[queue_kind(item)];
  }
  const parts: string[] = [];
  if (p.fields.length) {
    parts.push(`${p.fields.length} ${p.fields.length === 1 ? 'field' : 'fields'} changed`);
  }
  if (p.images_changed) parts.push('new photos');
  return parts.length ? parts.join(' · ') : 'An edit with nothing in it';
}

/**
 * Whether this submission could be approved as it stands.
 *
 * At least one photo, or it does not go on sale — the backend refuses the approval, so the
 * console must not offer a button that is going to come back 422. The rule applies to
 * first publication only: a product already live with no photo is a problem to chase, but
 * blocking its edit would hold up the correction rather than hide the product.
 */
export function publish_blocked(item: Pick<QueueItem, 'visible_photos' | 'approval_status'>): boolean {
  return item.visible_photos === 0 && item.approval_status !== 'APPROVED';
}

/** Whole days a submission has been waiting. */
export function days_waiting(item: Pick<QueueItem, 'waiting_since'>): number {
  return Math.floor((Date.now() - new Date(item.waiting_since).getTime()) / 86_400_000);
}

/**
 * How loudly to render the wait.
 *
 * Three days is where a vendor starts chasing support, so that is where the number stops
 * being neutral information and starts being the reason to open the row.
 */
export function wait_tone(days: number): 'default' | 'warning' | 'destructive' {
  if (days >= 7) return 'destructive';
  if (days >= 3) return 'warning';
  return 'default';
}

/** Field names as a human reads them, not as the column is spelled. */
export const FIELD_LABEL: Record<string, string> = {
  name: 'Name',
  brand: 'Brand',
  description: 'Description',
  category: 'Category',
  pack_size: 'Pack size',
  is_prescription_required: 'Prescription required',
};

/**
 * One side of a diff, as a human reads it.
 *
 * `field` matters because a proposed category arrives as the raw enum — a reviewer
 * comparing "WET_FOOD → TREATS" is being shown the database, not the change.
 */
export function field_value(v: unknown, field?: string): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (field === 'category') return humanize(String(v));
  return String(v);
}

/** A fee as one readable phrase — "10%" or "₹40". */
export function fee_amount(f: {
  mode: 'RATE' | 'FLAT' | null;
  rate_pct: number | null;
  amount_inr: number | null;
}): string {
  if (!f.mode) return 'No fee';
  return f.mode === 'FLAT' ? `₹${f.amount_inr ?? 0}` : `${f.rate_pct ?? 0}%`;
}

/** Where the fee came from, in words — a reviewer changing it needs to know what they override. */
export const SOURCE_LABEL: Record<FeeSource, string> = {
  VARIANT: 'Set for this size',
  PRODUCT: 'Set for this listing',
  CATALOG: 'From the catalogue product',
  CATEGORY: 'Category default',
  NONE: 'No fee set — the vendor keeps everything',
};

export const SOURCE_TONE: Record<FeeSource, StatusTone> = {
  VARIANT: 'warning',
  PRODUCT: 'warning',
  CATALOG: 'info',
  CATEGORY: 'neutral',
  NONE: 'destructive',
};
